<template>
  <div v-if="isOpen" class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-opacity duration-300">
    <div 
      :class="['max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 md:p-12 border-4 transition-all duration-500 shadow-2xl relative', isEasy ? 'bg-amber-50/95 border-amber-800/50' : 'card-dark-rich border-amber-600/60']"
      style="border-radius: 12px;"
      role="dialog"
      aria-labelledby="modal-title"
      aria-modal="true"
    >
      <!-- Close Button -->
      <button 
        @click="$emit('close')"
        :class="['absolute top-2 right-2 md:top-4 md:right-4 w-10 h-10 flex items-center justify-center text-2xl transition-colors rounded-full', isEasy ? 'text-amber-900 hover:bg-amber-200/50' : 'text-amber-200 hover:bg-stone-700/50']"
        aria-label="Close modal"
      >
        ✕
      </button>

      <div class="space-y-6 md:space-y-8">
        <div class="text-center">
          <Mascot size="w-24 h-24 md:w-48 md:h-48" mood="happy" />
          <h2 id="modal-title" :class="['text-3xl md:text-4xl font-serif-display mt-2 md:mt-4', isEasy ? 'ink-text' : 'text-amber-100']">
            How to <span :class="isEasy ? 'text-amber-800' : 'text-amber-500'">Play</span>
          </h2>
        </div>

        <div :class="['space-y-4 md:space-y-6 font-serif-body leading-relaxed text-sm md:text-base', isEasy ? 'text-stone-700' : 'text-stone-300']">
          <section>
            <h3 :class="['text-lg md:text-xl font-bold mb-1 md:mb-2', isEasy ? 'text-amber-900' : 'text-amber-300']">Objective</h3>
            <p>Solve as many riddles as you can before the time runs out! Each correct answer gives you extra time to keep the game going.</p>
          </section>

          <section class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div :class="['p-3 md:p-4 border-2 h-full', isEasy ? 'bg-amber-100/30 border-amber-700/30' : 'bg-stone-800/40 border-slate-600/40']" style="border-radius: 8px;">
              <h4 :class="['font-bold mb-1 md:mb-2 flex items-center gap-2', isEasy ? 'text-amber-900' : 'text-amber-200']">
                <span class="text-lg md:text-xl">⚡</span> Blitz Mode
              </h4>
              <p class="text-xs md:text-sm">Type your answers manually. This is for the ultimate riddle masters! Spelling counts, but we're flexible with things like "the" or "a".</p>
            </div>
            <div :class="['p-3 md:p-4 border-2 h-full', isEasy ? 'bg-green-100/20 border-green-700/30' : 'bg-green-900/10 border-green-700/30']" style="border-radius: 8px;">
              <h4 :class="['font-bold mb-1 md:mb-2 flex items-center gap-2', isEasy ? 'text-green-800' : 'text-green-300']">
                <span class="text-lg md:text-xl">🎈</span> Easy Mode
              </h4>
              <p class="text-xs md:text-sm">Perfect for younger players! Choose the correct answer from multiple choices. Great for practicing and learning new riddles.</p>
            </div>
          </section>

          <section>
            <h3 :class="['text-lg md:text-xl font-bold mb-1 md:mb-2', isEasy ? 'text-amber-900' : 'text-amber-300']">Attempts & Passes</h3>
            <p class="mb-1 md:mb-2">Stuck on a tricky one? You have a few ways to keep going:</p>
            <ul class="list-disc list-inside space-y-1 md:space-y-2 pl-2 mb-2 md:mb-4">
              <li><span class="font-bold text-red-600">Multiple Attempts:</span> You get {{ GAME_CONFIG.MAX_WRONG_ANSWERS }} tries for each riddle. Each wrong guess takes some time away ({{ GAME_CONFIG.WRONG_ANSWER_PENALTIES.join(', ') }}s).</li>
              <li><span class="font-bold text-blue-600">Pass Slips:</span> You have {{ GAME_CONFIG.MAX_PASSES }} pass slips per game. Using one lets you skip to the next riddle for a penalty of {{ GAME_CONFIG.PASS_PENALTY }}s.</li>
              <li><span class="font-bold text-amber-600">Auto-Pass:</span> If you miss a riddle {{ GAME_CONFIG.MAX_WRONG_ANSWERS }} times, it automatically uses a pass slip for you!</li>
            </ul>
          </section>

          <section>
            <h3 :class="['text-lg md:text-xl font-bold mb-1 md:mb-2', isEasy ? 'text-amber-900' : 'text-amber-300']">Scoring & Time</h3>
            <ul class="list-disc list-inside space-y-1 md:space-y-2 pl-2">
              <li><span class="font-bold text-stone-600">Starting Time:</span> {{ GAME_CONFIG.INITIAL_TIME }}s to solve as many as you can.</li>
              <li><span class="font-bold text-green-600">Correct Answer:</span> +{{ GAME_CONFIG.CORRECT_ANSWER_BONUS }}s and {{ isEasy ? GAME_CONFIG.SCORING.EASY : GAME_CONFIG.SCORING.MEDIUM }} points (plus streak bonuses!).</li>
              <li><span class="font-bold text-red-600">Pass Penalty:</span> -{{ GAME_CONFIG.PASS_PENALTY }}s when skipping a riddle.</li>
              <li><span class="font-bold text-blue-600">Time Cap:</span> Your time can't go above {{ GAME_CONFIG.MAX_TIME_CAP }}s.</li>
            </ul>
          </section>

          <section>
            <h3 :class="['text-lg md:text-xl font-bold mb-1 md:mb-2', isEasy ? 'text-amber-900' : 'text-amber-300']">Safety & Content</h3>
            <p class="text-xs md:text-sm italic">Collected Wit is designed to be child-friendly. All riddles are carefully selected to be appropriate for all ages.</p>
          </section>
        </div>

        <button 
          @click="$emit('close')"
          :class="['w-full py-3 md:py-4 text-lg md:text-xl font-serif-display font-bold transition-all transform hover:scale-[1.02] hand-drawn-shadow border-4', isEasy ? 'bg-amber-700 hover:bg-amber-600 border-amber-900 text-amber-50' : 'bg-amber-700 hover:bg-amber-500 border-amber-900 text-white']"
          style="border-radius: 8px; letter-spacing: 0.05em;"
        >
          Got it!
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import Mascot from './Mascot.vue'
import { GAME_CONFIG } from '../config'

const props = defineProps({
  isOpen: Boolean,
  mode: String
})

defineEmits(['close'])

const isEasy = computed(() => props.mode === 'easy')
</script>
